import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface JoinFormProps {
  onJoin: (name: string, password?: string) => void;
  isLoading: boolean;
  requiresPassword: boolean;
  defaultName?: string;
}

export const JoinForm = ({ onJoin, isLoading, requiresPassword, defaultName }: JoinFormProps) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = React.useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<{ name: string; password: string }>({
    defaultValues: { name: defaultName || '' }
  });

  const onSubmit = (data: { name: string; password: string }) => {
    onJoin(data.name, data.password || undefined);
  };

  return (
    <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] p-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.07)] max-w-[500px] mx-auto">
      <h3 className="font-body text-[18px] font-semibold text-[#111827] dark:text-white mb-[4px] text-center">
        {t('joinForm.title')}
      </h3>
      <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3] mb-[16px] text-center">
        {requiresPassword ? t('joinForm.descriptionPassword') : t('joinForm.description')}
      </p>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-[8px]">
        <div className="flex gap-[8px]">
          <input
            placeholder={t('joinForm.namePlaceholder')}
            {...register('name', { required: t('joinForm.nameRequired') })}
            className="flex-1 min-w-0 h-[44px] px-[16px] text-[16px] font-body text-base-black dark:text-white bg-white dark:bg-[#28282B] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] rounded-[24px] placeholder-gray-900 dark:placeholder-[#6B6B6F] focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] dark:focus:border-[#5DE1FF]/40 dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)] transition-all"
          />
          <button
            type="submit"
            disabled={isLoading}
            className={`h-[44px] px-[20px] rounded-[32px] font-body text-[14px] font-medium text-white transition-all duration-200 ${isLoading ? 'bg-gray-900 opacity-50 cursor-not-allowed' : 'bg-base-black dark:bg-[#C8C8CB] dark:text-[#1C1C1F] hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB]'}`}
          >
            {isLoading ? t('joinForm.joining') : t('joinForm.join')}
          </button>
        </div>
        {requiresPassword && (
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder={t('joinForm.passwordPlaceholder')}
              {...register('password', { required: requiresPassword ? t('joinForm.passwordRequired') : false })}
              className="w-full h-[44px] px-[16px] pr-[44px] text-[16px] font-body text-base-black dark:text-white bg-white dark:bg-[#28282B] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] rounded-[24px] placeholder-gray-900 dark:placeholder-[#6B6B6F] focus:outline-none focus:border-[#BFBFC0] focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] dark:focus:border-[#5DE1FF]/40 dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)] transition-all"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-[14px] top-1/2 -translate-y-1/2 text-[#818185] dark:text-[#A0A0A3] hover:text-base-black dark:hover:text-white transition-colors"
            >
              {showPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        )}
      </form>
      {errors.name && (
        <p className="text-red-500 text-sm font-body mt-[8px] ml-[6px]">
          {errors.name.message}
        </p>
      )}
      {errors.password && (
        <p className="text-red-500 text-sm font-body mt-[8px] ml-[6px]">
          {errors.password.message}
        </p>
      )}
    </div>
  );
};
